import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ref, onValue, set } from 'firebase/database';
import { database } from '../firebaseConfig';
import { useActiveWorld } from '../hooks/useActiveWorld';
import {
  applyListLayout,
  canMoveList,
  moveList,
  normalizeImportItems,
  sanitizeLayout,
  setListHidden,
} from './importListLayout';
import './WorldDbImportFilter.css';

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

const formatDate = (isoString) => {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return ''; }
};

const isNew = (isoString) => {
  if (!isoString) return false;
  try {
    return (Date.now() - new Date(isoString).getTime()) < SEVEN_DAYS_MS;
  } catch { return false; }
};

const layoutPath = (userId) => `World/${userId}/settings/importListLayout`;

const DotsIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="12" cy="5" r="1.8" />
    <circle cx="12" cy="12" r="1.8" />
    <circle cx="12" cy="19" r="1.8" />
  </svg>
);

const WorldDbImportFilter = ({ currentImportFilter, onImportFilterChange, isLoading, importOptions = [] }) => {
  const { userId, canWrite } = useActiveWorld();
  const canEditLayout = canWrite();
  const [open, setOpen] = useState(false);
  const [hiddenOpen, setHiddenOpen] = useState(false);
  const [layout, setLayout] = useState({ hidden: [], order: [] });
  const [menu, setMenu] = useState(null);
  const refEl = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      const inFilter = refEl.current && refEl.current.contains(e.target);
      const inMenu = e.target.closest?.('.wdif-menu');
      if (!inFilter && !inMenu) {
        setOpen(false);
        setMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (!userId) return undefined;
    const layoutRef = ref(database, layoutPath(userId));
    const unsub = onValue(layoutRef, (snap) => {
      setLayout(sanitizeLayout(snap.val()));
    });
    return () => unsub();
  }, [userId]);

  const items = useMemo(() => normalizeImportItems(importOptions), [importOptions]);
  const { visible, hidden } = useMemo(() => applyListLayout(items, layout), [items, layout]);

  const selectedItem = items.find((i) => i.name === currentImportFilter);
  const isAll = !currentImportFilter || currentImportFilter === 'all';
  const displayLabel = isAll ? 'All Imports' : (selectedItem?.name || currentImportFilter);

  useEffect(() => {
    if (!open) setHiddenOpen(false);
  }, [open]);

  const persistLayout = useCallback(async (next) => {
    const sanitized = sanitizeLayout(next);
    setLayout(sanitized);
    setMenu(null);
    if (!userId || !canEditLayout) return;
    try {
      await set(ref(database, layoutPath(userId)), sanitized);
    } catch (err) {
      console.error('Failed to save import list layout:', err);
    }
  }, [userId, canEditLayout]);

  const handleSelect = (value) => {
    onImportFilterChange(value);
    setOpen(false);
    setMenu(null);
  };

  const openMenu = (e, item, group) => {
    e.stopPropagation();
    if (!canEditLayout) return;
    const rect = e.currentTarget.getBoundingClientRect();
    if (menu?.name === item.name) {
      setMenu(null);
      return;
    }
    const menuWidth = 168;
    const left = Math.min(rect.left, window.innerWidth - menuWidth - 8);
    setMenu({
      name: item.name,
      hidden: group === 'hidden',
      top: rect.bottom + 4,
      left: Math.max(8, left),
    });
  };

  const renderItem = (item, group, index, total) => {
    const active = item.name === currentImportFilter;
    return (
      <div
        key={item.name}
        className={`wdif-option ${active ? 'wdif-option-selected' : ''} ${group === 'hidden' ? 'wdif-option-hidden' : ''}`}
        onClick={() => handleSelect(item.name)}
      >
        <span className={`wdif-radio ${active ? 'wdif-radio-active' : ''}`} />
        <div className="wdif-option-content">
          <div className="wdif-option-name-row">
            <span className="wdif-option-name">{item.name}</span>
            {isNew(item.last_imported) && (
              <span className="wdif-meta-badge wdif-meta-new">NEW</span>
            )}
            {canEditLayout && (
              <button
                type="button"
                className={`wdif-kebab ${menu?.name === item.name ? 'wdif-kebab-open' : ''}`}
                aria-label={`Options for ${item.name}`}
                onClick={(e) => openMenu(e, item, group)}
              >
                <DotsIcon />
              </button>
            )}
          </div>
          <div className="wdif-option-meta">
            {item.imported_count != null && (
              <span className="wdif-meta-badge wdif-meta-count">
                {item.imported_count} contacts
              </span>
            )}
            {item.template_name && (
              <span className="wdif-meta-badge wdif-meta-template">
                {item.template_name}
              </span>
            )}
            {item.last_imported && (
              <span className="wdif-meta-date">{formatDate(item.last_imported)}</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="wdif-wrapper" ref={refEl}>
      <button
        className={`wdif-trigger ${open ? 'wdif-trigger-open' : ''} ${!isAll ? 'wdif-trigger-active' : ''}`}
        onClick={() => {
          if (isLoading) return;
          setOpen((p) => !p);
          setMenu(null);
        }}
        disabled={isLoading}
        title="Filter by import list"
      >
        {!isAll && <span className="wdif-trigger-dot" />}
        <span className="wdif-trigger-label">{displayLabel}</span>
        <svg className={`wdif-trigger-chevron ${open ? 'wdif-trigger-chevron-open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="wdif-dropdown">
          <div
            className={`wdif-option wdif-option-all ${isAll ? 'wdif-option-selected' : ''}`}
            onClick={() => handleSelect('all')}
          >
            <span className={`wdif-radio ${isAll ? 'wdif-radio-active' : ''}`} />
            <span className="wdif-option-all-label">All Imports</span>
            <span className="wdif-option-all-count">{items.length} lists</span>
          </div>

          {(visible.length > 0 || hidden.length > 0) && <div className="wdif-divider" />}

          <div className="wdif-list" onScroll={() => setMenu(null)}>
            {visible.map((item, index) => renderItem(item, 'visible', index, visible.length))}

            {hidden.length > 0 && (
              <>
                <button
                  type="button"
                  className={`wdif-hidden-toggle ${hiddenOpen ? 'wdif-hidden-toggle-open' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setHiddenOpen((p) => !p);
                    setMenu(null);
                  }}
                >
                  <svg className="wdif-hidden-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                  <span className="wdif-hidden-toggle-label">Hidden</span>
                  <span className="wdif-hidden-toggle-count">{hidden.length} {hidden.length === 1 ? 'list' : 'lists'}</span>
                </button>
                {hiddenOpen && hidden.map((item, index) => renderItem(item, 'hidden', index, hidden.length))}
              </>
            )}
          </div>

          {items.length === 0 && (
            <div className="wdif-empty">No imports yet</div>
          )}
        </div>
      )}

      {open && menu && createPortal((
        <div
          className="wdif-menu"
          style={{ top: menu.top, left: menu.left }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="wdif-menu-item"
            disabled={!canMoveList(layout, items, menu.name, 'up')}
            onClick={() => persistLayout(moveList(layout, items, menu.name, 'up'))}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="18 15 12 9 6 15" />
            </svg>
            Move up
          </button>
          <button
            type="button"
            className="wdif-menu-item"
            disabled={!canMoveList(layout, items, menu.name, 'down')}
            onClick={() => persistLayout(moveList(layout, items, menu.name, 'down'))}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="6 9 12 15 18 9" />
            </svg>
            Move down
          </button>
          <button
            type="button"
            className="wdif-menu-item"
            onClick={() => persistLayout(setListHidden(layout, items, menu.name, !menu.hidden))}
          >
            {menu.hidden ? (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            ) : (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                <line x1="1" y1="1" x2="23" y2="23" />
              </svg>
            )}
            {menu.hidden ? 'Show list' : 'Hide list'}
          </button>
        </div>
      ), document.body)}
    </div>
  );
};

export default WorldDbImportFilter;
